import { useId } from 'react';
import { useFieldControlId, useResolvedAriaLabel } from './fieldControlContext';
/**
 * ToggleSwitch - Universal toggle switch for boolean states
 *
 * Default: 36px × 20px with 16px thumb.
 * Compact: 28px × 14px with 10px thumb — for dense toolbars.
 *
 * @param {boolean} checked - Current toggle state
 * @param {function} onChange - Callback when toggle changes (receives new boolean value)
 * @param {string} label - Optional label text displayed next to toggle
 * @param {boolean} disabled - Whether toggle is disabled
 * @param {boolean} subtle - Use muted gray instead of accent color when on
 * @param {boolean} compact - Smaller toggle for toolbar use
 * @param {string} onColor - Optional custom track color when enabled
 * @param {string} offColor - Optional custom track color when disabled
 * @param {string} className - Additional CSS classes for the container
 */
const ToggleSwitch = ({
  id,
  checked = false,
  onChange,
  label = null,
  ariaLabel = undefined,
  disabled = false,
  subtle = false,
  compact = false,
  onColor = null,
  offColor = null,
  className = ''
}) => {
  const fallbackId = useId();
  const controlId = useFieldControlId(id, fallbackId);
  // A `<label for>` now names the switch, from this component's own label or a
  // FormField caption. Keep an aria-label only where it says more than that.
  const resolvedAriaLabel = useResolvedAriaLabel(ariaLabel, id, label);

  const handleClick = () => {
    if (!disabled && onChange) {
      onChange(!checked);
    }
  };

  const offBg = offColor || (subtle ? 'var(--bg-app)' : 'var(--bg-button-disabled)');
  const onBg = onColor || (subtle ? 'var(--text-muted)' : 'var(--toggle-track-on)');

  return (
    <div
      className={`toggle-switch flex items-center gap-2 ${className}`}
      data-compact={compact ? 'true' : undefined}
    >
      <button
        id={controlId}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={resolvedAriaLabel}
        onClick={handleClick}
        disabled={disabled}
        className="toggle-switch__track relative transition-all flex-shrink-0"
        style={{
          boxSizing: 'border-box',
          borderRadius: 'var(--radius-full)',
          backgroundColor: disabled ? 'var(--bg-button-disabled)' : checked ? onBg : offBg,
          cursor: disabled ? 'not-allowed' : 'pointer',
          border: 'none',
          padding: 0,
          opacity: disabled ? 0.5 : 1,
          boxShadow: checked ? 'inset 0 0 0 1px var(--toggle-track-on-border)' : 'none',
        }}
      >
        <div
          className="toggle-switch__thumb"
          style={{
            borderRadius: '50%',
            backgroundColor: checked ? 'var(--toggle-thumb-on)' : 'var(--toggle-thumb-off)',
            boxShadow: '0 1px 3px rgba(var(--shadow-color), 0.3)',
            transition: 'left var(--duration-normal) ease-in-out, background-color var(--duration-normal) ease-in-out'
          }}
        />
      </button>
      {label && (
        <label
          htmlFor={controlId}
          className={compact ? undefined : 'param-label'}
          style={{
            marginBottom: 0,
            ...(compact && {
              fontSize: 'var(--text-caption)',
              lineHeight: 1,
              color: 'var(--text-secondary)',
              userSelect: 'none',
            }),
          }}
        >
          {label}
        </label>
      )}
    </div>
  );
};

export default ToggleSwitch;
